import { Getter, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { ViewComponentUuid, ViewSectionUuid } from '../../../data-models/view-config.data-model';
import { ViewModel } from '../../../models/view.model';
import { selectedViewPFLoadableJ } from '../state/ViewStateJ';
import { useUpdateSelectedView } from './useViewActions';

function readSelectedViewSync(get: Getter): ViewModel | null {
  const loadable = get(selectedViewPFLoadableJ);
  return loadable.state === 'hasData' ? loadable.data : null;
}

function readLayoutView(get: Getter, rendered: ViewModel | undefined): ViewModel | null {
  return rendered ?? readSelectedViewSync(get);
}

/**
 * The view a section lays itself out from, given the view it renders.
 *
 * - Another view selected: the rendered view. PortfolioOverview keeps rendering the previous view
 *   through a switch (it defers the view), and a section can mount after the selection has moved on;
 *   its data follow the view it renders, so its sections and charts must too.
 * - The same view selected: the selected copy, which holds an edit as soon as it is saved. The page
 *   passes the section the edited view one deferred render later, and a second pick or drag in between
 *   would otherwise build on the list as it was and drop the first.
 */
export function useLayoutView(rendered: ViewModel): ViewModel {
  const selected = useAtomValue(selectedViewPFLoadableJ);
  return selected.state === 'hasData' && selected.data?.id === rendered.id ? selected.data : rendered;
}

export function useView() {
  const updateSelectedView = useUpdateSelectedView();

  const getSection = useAtomCallback(
    useCallback((get, _set, section: ViewSectionUuid, rendered?: ViewModel) => {
      const view = readLayoutView(get, rendered);

      if (!view?.sections?.[section]) {
        throw new Error(`There is no ${section} view section config!`);
      }

      return view.sections[section];
    }, [])
  );

  const getSectionsOrder = useAtomCallback(
    useCallback((get, _set, rendered?: ViewModel) => {
      const view = readLayoutView(get, rendered);
      if (!view?.order?.sections) {
        return [];
      }

      return view.order.sections;
    }, [])
  );

  const getComponentsOrder = useAtomCallback(
    useCallback((get, _set, section: ViewSectionUuid, rendered?: ViewModel) => {
      const view = readLayoutView(get, rendered);
      if (!view?.order?.components?.[section]) {
        return [];
      }
      return view.order.components[section];
    }, [])
  );

  const setComponentsOrder = useAtomCallback(
    useCallback(
      (get, _set, section: ViewSectionUuid, order: ViewComponentUuid[], rendered?: ViewModel) => {
        const view = readSelectedViewSync(get);
        // An order a section built from the view it renders belongs to that view only: a drag or a pick
        // that lands while the page is switching views must not be written into the next one.
        if (view && (!rendered || rendered.id === view.id)) {
          // view.order is undefined at runtime for views seeded with an empty config (e.g. fund_overview).
          updateSelectedView({
            order: { ...view.order, components: { ...view.order?.components, [section]: order } },
          });
        }
      },
      [updateSelectedView]
    )
  );

  const getFundsFilter = useAtomCallback(
    useCallback((get) => {
      const view = readSelectedViewSync(get);
      return view?.filteredFunds || null;
    }, [])
  );

  const setFundsFilter = useCallback(
    (filteredFunds: IFundDataModel[] | null) => {
      updateSelectedView({ filteredFunds });
    },
    [updateSelectedView]
  );

  return useMemo(
    () => ({
      isLoading: false,

      getSection,
      getSectionsOrder,
      getComponentsOrder,
      setComponentsOrder,
      isFetching: false,
      setFundsFilter,
      getFundsFilter,
    }),
    [getSection, getSectionsOrder, getComponentsOrder, setComponentsOrder, setFundsFilter, getFundsFilter]
  );
}
